/**
 * O gênero provável de uma pessoa, para escolher entre "Bem-vinda" e
 * "Bem-vindo" (06/10/2026).
 *
 * O que a pessoa declarou vale primeiro (`genero` da conta). Sem isso, o
 * primeiro nome é procurado na lista do IBGE (`generos-dos-nomes.ts`, gerada
 * do Censo 2010). Fora da lista, a terminação decide: "a" é feminino, "o"
 * (menos "ao") é masculino — medido sobre a própria lista, erra em 4 de 529.
 * O resto fica sem palpite (`null`), e quem chama usa um texto neutro.
 *
 * É palpite, não dado: serve para o tom de uma frase, nunca para decidir
 * acesso, jornada ou cuidado.
 */
import { NOMES_FEMININOS, NOMES_MASCULINOS } from './generos-dos-nomes';

export type GeneroProvavel = 'F' | 'M';

const FEMININOS = new Set(NOMES_FEMININOS.split(' '));
const MASCULINOS = new Set(NOMES_MASCULINOS.split(' '));

/** Primeiro nome em minúsculas, sem acento e só com letras: "Ana-Clara" → "ana". */
export function primeiroNomeNormalizado(nome?: string | null): string {
  const primeiro = String(nome ?? '').trim().split(/[\s-]+/)[0] ?? '';
  return primeiro
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

export function generoPeloNome(nome?: string | null): GeneroProvavel | null {
  const n = primeiroNomeNormalizado(nome);
  if (n.length < 2) return null;
  if (FEMININOS.has(n)) return 'F';
  if (MASCULINOS.has(n)) return 'M';
  if (n.endsWith('a')) return 'F';
  if (n.endsWith('o') && !n.endsWith('ao')) return 'M';
  return null;
}

/** O declarado vence o nome; "outro" e "não informado" não viram palpite. */
export function generoDaPessoa(pessoa?: { genero?: string | null; preferredName?: string | null; fullName?: string | null } | null): GeneroProvavel | null {
  if (pessoa?.genero === 'FEMININO') return 'F';
  if (pessoa?.genero === 'MASCULINO') return 'M';
  if (pessoa?.genero === 'OUTRO') return null;
  return generoPeloNome(pessoa?.preferredName || pessoa?.fullName);
}
